import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Alert, CircularProgress, Skeleton, Checkbox, Chip
} from '@mui/material';
import { ArrowBack, Print, Receipt } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';

const fmt2 = v => parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

// ── Date range presets (10-day periods) ─────────────────────
function getDateRanges() {
  const today = new Date();
  const year  = today.getFullYear();
  const month = today.getMonth();
  const ranges = [];
  // Last 6 periods of 10 days
  for (let m = month; m >= Math.max(0, month - 2); m--) {
    const periods = [[1,10],[11,20],[21, new Date(year, m+1, 0).getDate()]];
    for (let i = periods.length - 1; i >= 0; i--) {
      const [s, e] = periods[i];
      const from = new Date(year, m, s);
      const to   = new Date(year, m, e);
      if (from <= today) {
        ranges.push({
          label: `${s.toString().padStart(2,'0')} ${from.toLocaleString('en',{month:'short'})} ${year} - ${e.toString().padStart(2,'0')} ${to.toLocaleString('en',{month:'short'})} ${year}`,
          from:  from.toISOString().split('T')[0],
          to:    to.toISOString().split('T')[0],
        });
      }
    }
  }
  return ranges.reverse().slice(-9);
}

// ── Print function ───────────────────────────────────────────
function printInvoice(rows, centerName, fromDate, toDate, isPending) {
  const title = isPending ? 'Milk Sale Pending Invoices' : 'Milk Sale Invoices';
  const totLiter  = rows.reduce((s,r)=>s+parseFloat(r.liter||0),0);
  const totAmt    = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const totCash   = rows.reduce((s,r)=>s+parseFloat(r.cashReceived||0),0);
  const totPrev   = rows.reduce((s,r)=>s+parseFloat(r.prevAmount||0),0);
  const totTotal  = rows.reduce((s,r)=>s+parseFloat(r.totalAmount||0),0);

  const html = `<!DOCTYPE html><html><head>
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
    h2 { text-align: center; margin-bottom: 4px; font-size: 16px; }
    .sub { text-align: center; font-size: 11px; color: #555; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: right; }
    th { background: #f0f0f0; font-weight: 700; text-align: center; }
    td:nth-child(2), td:nth-child(3) { text-align: left; }
    .total td { font-weight: 700; background: #f9f9f9; }
    @media print { button { display: none; } }
  </style></head><body>
  <h2>${centerName}</h2>
  <p class="sub">${title} | ${fromDate} to ${toDate}</p>
  <table>
    <thead><tr>
      <th>#</th><th>Code</th><th>Name</th>
      <th>Liter</th><th>Amount</th><th>Cash Received</th>
      <th>Prev Amount</th><th>Total Amount</th>
    </tr></thead>
    <tbody>
      ${rows.map((r,i)=>`<tr>
        <td style="text-align:center">${i+1}</td>
        <td style="text-align:left">${r.code}</td>
        <td style="text-align:left">${r.name}</td>
        <td>${parseFloat(r.liter).toFixed(2)}</td>
        <td>${fmt2(r.amount)}</td>
        <td>${fmt2(r.cashReceived)}</td>
        <td>${fmt2(r.prevAmount)}</td>
        <td>${fmt2(r.totalAmount)}</td>
      </tr>`).join('')}
      <tr class="total">
        <td colspan="3" style="text-align:left">Total</td>
        <td>${totLiter.toFixed(2)}</td>
        <td>${fmt2(totAmt)}</td>
        <td>${fmt2(totCash)}</td>
        <td>${fmt2(totPrev)}</td>
        <td>${fmt2(totTotal)}</td>
      </tr>
    </tbody>
  </table>
  <script>window.onload=()=>window.print();</script>
  </body></html>`;
  const w = window.open('','_blank');
  w.document.write(html);
  w.document.close();
}

// ════════════════════════════════════════════════════════════
//  MAIN EXPORT
// ════════════════════════════════════════════════════════════
export default function MilkSaleInvoice() {
  const [view, setView] = useState('invoices'); // invoices | pending
  if (view === 'pending')
    return <PendingInvoices onBack={() => setView('invoices')} />;
  return <InvoiceList onPending={() => setView('pending')} />;
}

// ════════════════════════════════════════════════════════════
//  VIEW 1: Milk Sale Invoices (projected + generate)
// ════════════════════════════════════════════════════════════
function InvoiceList({ onPending }) {
  const { theme, centerInfo } = useApp();
  const centerName = centerInfo?.centerName || 'Dairy Center';

  const ranges    = getDateRanges();
  const [rangeIdx, setRangeIdx] = useState(ranges.length - 1);
  const range = ranges[rangeIdx] || ranges[0];

  const [rows,      setRows]     = useState([]);
  const [total,     setTotal]    = useState(0);
  const [loading,   setLoading]  = useState(false);
  const [selected,  setSelected] = useState([]);
  const [search,    setSearch]   = useState('');
  const [generating,setGen]      = useState(false);
  const [error,     setError]    = useState('');
  const [success,   setSuccess]  = useState('');

  const fetchData = useCallback(async () => {
    if (!range) return;
    setLoading(true); setSelected([]);
    try {
      const res = await api.get('/milk-sale-invoice/projected', {
        params: { fromDate: range.from, toDate: range.to, search, page: 1, pageSize: 100 }
      });
      if (res.data?.success) {
        const data = res.data.data?.data || [];
        // normalize keys (API returns PascalCase via Dictionary)
        const normalized = data.map(r => ({
          customerId:   r.CustomerId   ?? r.customerId,
          code:         r.Code         ?? r.code,
          name:         r.Name         ?? r.name,
          liter:        r.Liter        ?? r.liter        ?? 0,
          amount:       r.Amount       ?? r.amount       ?? 0,
          cashReceived: r.CashReceived ?? r.cashReceived ?? 0,
          prevAmount:   r.PrevAmount   ?? r.prevAmount   ?? 0,
          totalAmount:  r.TotalAmount  ?? r.totalAmount  ?? 0,
        }));
        setRows(normalized);
        setTotal(res.data.data?.totalCount ?? normalized.length);
      } else setError(res.data?.message || 'Load failed.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [range, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allChecked = rows.length > 0 && selected.length === rows.length;
  const toggleAll  = () => setSelected(allChecked ? [] : rows.map(r => r.customerId));
  const toggleOne  = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const handleGenerate = async () => {
    const toGenerate = selected.length > 0 ? rows.filter(r => selected.includes(r.customerId)) : rows;
    if (!toGenerate.length) { setError('No rows to generate.'); return; }
    setGen(true);
    try {
      const res = await api.post('/milk-sale-invoice/generate', {
        fromDate: range.from, toDate: range.to,
        rows: toGenerate.map(r => ({
          customerId:   r.customerId,
          liter:        parseFloat(r.liter)        || 0,
          amount:       parseFloat(r.amount)       || 0,
          cashReceived: parseFloat(r.cashReceived) || 0,
          prevAmount:   parseFloat(r.prevAmount)   || 0,
          totalAmount:  parseFloat(r.totalAmount)  || 0,
        }))
      });
      if (res.data?.success) {
        setSuccess(res.data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else setError(res.data?.message || 'Generate failed.');
    } catch (e) { setError(e.message); }
    finally { setGen(false); }
  };

  const totLiter  = rows.reduce((s,r)=>s+parseFloat(r.liter||0),0);
  const totAmount = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const totCash   = rows.reduce((s,r)=>s+parseFloat(r.cashReceived||0),0);
  const totPrev   = rows.reduce((s,r)=>s+parseFloat(r.prevAmount||0),0);
  const totTotal  = rows.reduce((s,r)=>s+parseFloat(r.totalAmount||0),0);

  const thS = (align='left') => ({
    fontSize:'0.8rem', fontWeight:700, color:'#374151', padding:'9px 12px',
    borderBottom:'2px solid #e5e7eb', background:'#f9fafb', whiteSpace:'nowrap', textAlign:align,
  });
  const tdS = (bold,align='left',color) => ({
    fontSize:'0.82rem', padding:'8px 12px', borderBottom:'1px solid #f3f4f6',
    whiteSpace:'nowrap', fontWeight:bold?700:400, textAlign:align, color:color||'#111827',
  });

  return (
    <Layout title="Milk Sale Invoices">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2.5 }}>
        <Receipt sx={{ color:theme.primary }}/>
        <Typography variant="h6" sx={{ fontWeight:800 }}>
          Milk Sale Invoices ({total})
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Date Range :</Typography>
          <select value={rangeIdx} onChange={e => setRangeIdx(Number(e.target.value))}
            style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #d1d5db',
              fontSize:'0.82rem', background:'#fff', cursor:'pointer' }}>
            {ranges.map((r,i) => (
              <option key={i} value={i}>{r.label}</option>
            ))}
          </select>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <Button variant="outlined" startIcon={<Print/>}
            onClick={() => printInvoice(rows, centerName, range.from, range.to, false)}
            sx={{ borderRadius:2, textTransform:'none', fontWeight:600,
              borderColor:theme.primary, color:theme.primary }}>
            Print
          </Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating}
            sx={{ borderRadius:2, textTransform:'none', fontWeight:700,
              background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
            {generating ? <CircularProgress size={16} sx={{ color:'#fff' }}/> : 'Generate Invoice'}
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>
            Milk Sale Invoices
          </Typography>
          <TextField size="small" placeholder="Search Invoice"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width:200, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS('center'), width:40 }}>
                  <Checkbox size="small" checked={allChecked} indeterminate={selected.length>0&&!allChecked}
                    onChange={toggleAll} sx={{ p:0 }}/>
                </th>
                <th style={{ ...thS(), width:80 }}>Code ↕</th>
                <th style={thS()}>Name ↕</th>
                <th style={thS('right')}>Liter ↕</th>
                <th style={thS('right')}>Amount ↕</th>
                <th style={thS('right')}>Cash Received ↕</th>
                <th style={thS('right')}>Prev Amount ↕</th>
                <th style={thS('right')}>Total Amount ↕</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:4}).map((_,i)=>(
                    <tr key={i}>{Array.from({length:8}).map((__,j)=>(
                      <td key={j} style={{ padding:'8px 12px' }}><Skeleton height={14}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign:'center', padding:'40px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Records Found.
                    </td></tr>
                  : <>
                      {rows.map((row, idx) => (
                        <tr key={row.customerId}
                          style={{ background: selected.includes(row.customerId) ? '#eff6ff' : idx%2===0?'#fff':'#fafafa',
                            cursor:'pointer' }}
                          onClick={() => toggleOne(row.customerId)}>
                          <td style={{ ...tdS(), textAlign:'center' }}>
                            <Checkbox size="small" checked={selected.includes(row.customerId)}
                              onChange={() => toggleOne(row.customerId)} sx={{ p:0 }}
                              onClick={e => e.stopPropagation()}/>
                          </td>
                          <td style={tdS(true)}>{row.code}</td>
                          <td style={tdS(false)}>{row.name}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.liter).toFixed(2)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.amount)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.cashReceived)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.prevAmount)}</td>
                          <td style={tdS(true,'right')}>{fmt2(row.totalAmount)}</td>
                        </tr>
                      ))}
                      {/* Totals */}
                      <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                        <td style={tdS()}></td>
                        <td style={tdS(true)}>-</td>
                        <td style={tdS(true)}>Total</td>
                        <td style={tdS(true,'right')}>{totLiter.toFixed(2)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totAmount)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totCash)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totPrev)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totTotal)}</td>
                      </tr>
                    </>
              }
            </tbody>
          </table>
        </Box>

        {/* Footer */}
        <Box sx={{ px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280' }}>
            Showing 1 to {rows.length} of {total} entries
          </Typography>
          <Button size="small" onClick={onPending}
            sx={{ textTransform:'none', fontSize:'0.78rem', color:theme.primary, fontWeight:600 }}>
            View Pending Invoices →
          </Button>
        </Box>
      </Card>
    </Layout>
  );
}

// ════════════════════════════════════════════════════════════
//  VIEW 2: Milk Sale Pending Invoices
// ════════════════════════════════════════════════════════════
function PendingInvoices({ onBack }) {
  const { theme, centerInfo } = useApp();
  const centerName = centerInfo?.centerName || 'Dairy Center';

  const ranges    = getDateRanges();
  const [rangeIdx, setRangeIdx] = useState(ranges.length - 1);
  const range = ranges[rangeIdx] || ranges[0];

  const [rows,     setRows]    = useState([]);
  const [total,    setTotal]   = useState(0);
  const [loading,  setLoading] = useState(false);
  const [selected, setSelected]= useState([]);
  const [search,   setSearch]  = useState('');
  const [error,    setError]   = useState('');

  const fetchData = useCallback(async () => {
    if (!range) return;
    setLoading(true); setSelected([]);
    try {
      const res = await api.get('/milk-sale-invoice/pending', {
        params: { fromDate: range.from, toDate: range.to, search }
      });
      if (res.data?.success) {
        const data = res.data.data?.data || [];
        const normalized = data.map(r => ({
          invoiceId:    r.InvoiceId    ?? r.invoiceId,
          customerId:   r.CustomerId   ?? r.customerId,
          code:         r.Code         ?? r.code,
          name:         r.Name         ?? r.name,
          liter:        r.Liter        ?? r.liter        ?? 0,
          amount:       r.Amount       ?? r.amount       ?? 0,
          cashReceived: r.CashReceived ?? r.cashReceived ?? 0,
          prevAmount:   r.PrevAmount   ?? r.prevAmount   ?? 0,
          totalAmount:  r.TotalAmount  ?? r.totalAmount  ?? 0,
        }));
        setRows(normalized);
        setTotal(normalized.length);
      } else setError(res.data?.message || 'Load failed.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [range, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allChecked = rows.length > 0 && selected.length === rows.length;
  const toggleAll  = () => setSelected(allChecked ? [] : rows.map(r => r.customerId));
  const toggleOne  = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const totLiter  = rows.reduce((s,r)=>s+parseFloat(r.liter||0),0);
  const totAmount = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const totCash   = rows.reduce((s,r)=>s+parseFloat(r.cashReceived||0),0);
  const totPrev   = rows.reduce((s,r)=>s+parseFloat(r.prevAmount||0),0);
  const totTotal  = rows.reduce((s,r)=>s+parseFloat(r.totalAmount||0),0);

  const printSelected = () => {
    const toPrint = selected.length > 0 ? rows.filter(r => selected.includes(r.customerId)) : rows;
    printInvoice(toPrint, centerName, range.from, range.to, true);
  };

  const thS = (align='left') => ({
    fontSize:'0.8rem', fontWeight:700, color:'#374151', padding:'9px 12px',
    borderBottom:'2px solid #e5e7eb', background:'#f9fafb', whiteSpace:'nowrap', textAlign:align,
  });
  const tdS = (bold,align='left',color) => ({
    fontSize:'0.82rem', padding:'8px 12px', borderBottom:'1px solid #f3f4f6',
    whiteSpace:'nowrap', fontWeight:bold?700:400, textAlign:align, color:color||'#111827',
  });

  return (
    <Layout title="Milk Sale Pending Invoices">
      {error && <Alert severity="error" sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2.5 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <IconButton size="small" onClick={onBack} sx={{ color:theme.primary }}>
            <ArrowBack/>
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Milk Sale Pending Invoices</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Print/>} onClick={printSelected}
          sx={{ borderRadius:2, textTransform:'none', fontWeight:600,
            borderColor:theme.primary, color:theme.primary }}>
          Print {selected.length > 0 ? `(${selected.length})` : ''}
        </Button>
      </Box>

      {/* Date Range */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
        <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Date Range :</Typography>
        <select value={rangeIdx} onChange={e => setRangeIdx(Number(e.target.value))}
          style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #d1d5db',
            fontSize:'0.82rem', background:'#fff', cursor:'pointer' }}>
          {ranges.map((r,i) => (
            <option key={i} value={i}>{r.label}</option>
          ))}
        </select>
      </Box>

      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>
            Milk Sale Pending Invoices
          </Typography>
          <TextField size="small" placeholder="Search Invoice"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width:200, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS('center'), width:40 }}>
                  <Checkbox size="small" checked={allChecked} indeterminate={selected.length>0&&!allChecked}
                    onChange={toggleAll} sx={{ p:0 }}/>
                </th>
                <th style={{ ...thS(), width:80 }}>Code ↕</th>
                <th style={thS()}>Name ↕</th>
                <th style={thS('right')}>Liter ↕</th>
                <th style={thS('right')}>Amount ↕</th>
                <th style={thS('right')}>Cash Received ↕</th>
                <th style={thS('right')}>Prev Amount ↕</th>
                <th style={thS('right')}>Total Amount ↕</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:4}).map((_,i)=>(
                    <tr key={i}>{Array.from({length:8}).map((__,j)=>(
                      <td key={j} style={{ padding:'8px 12px' }}><Skeleton height={14}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign:'center', padding:'40px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Pending Invoices Found.
                    </td></tr>
                  : <>
                      {rows.map((row, idx) => (
                        <tr key={row.customerId}
                          style={{ background: selected.includes(row.customerId) ? '#eff6ff' : idx%2===0?'#fff':'#fafafa',
                            cursor:'pointer' }}
                          onClick={() => toggleOne(row.customerId)}>
                          <td style={{ ...tdS(), textAlign:'center' }}>
                            <Checkbox size="small" checked={selected.includes(row.customerId)}
                              onChange={() => toggleOne(row.customerId)} sx={{ p:0 }}
                              onClick={e => e.stopPropagation()}/>
                          </td>
                          <td style={tdS(true)}>{row.code}</td>
                          <td style={tdS(false)}>{row.name}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.liter).toFixed(2)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.amount)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.cashReceived)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.prevAmount)}</td>
                          <td style={tdS(true,'right')}>{fmt2(row.totalAmount)}</td>
                        </tr>
                      ))}
                      {/* Totals */}
                      <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                        <td style={tdS()}></td>
                        <td style={tdS(true)}>Total</td>
                        <td style={tdS()}>-</td>
                        <td style={tdS(true,'right')}>{totLiter.toFixed(2)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totAmount)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totCash)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totPrev)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totTotal)}</td>
                      </tr>
                    </>
              }
            </tbody>
          </table>
        </Box>

        <Box sx={{ px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280' }}>
            Showing 1 to {rows.length} of {total} entries
          </Typography>
        </Box>
      </Card>
    </Layout>
  );
}
